import { useEffect, useMemo, useState } from 'react';
import { mapProductIdToTag } from '@/components/utils/utils';
import {
  useGetFeatureKeys,
  useGetSubscriptionStatusQuery,
} from '@viome/viome-base/src/usecase/Misc/MiscQuery';
import { KitType } from '@viome/viome-base/src/usecase/Bootup/BootupModel';
import { isRecentlyPublished } from '@/components/utils/Timezone';
import { useDynamicConfig } from '@statsig/react-bindings';
import { useBootup } from '@/hooks/useBootup';

const useStorifyConfig = (isSingleStory = false) => {
  const config = useDynamicConfig('public_config');
  const isInternalTesting = config.get('storify_internal_testing', false);
  const { bootupState } = useBootup();
  const kitType = bootupState?.kitType;
  const rnrLastGeneratedDate = bootupState?.rnrLastGeneratedDate;
  const isResultsReleasedRecently = isRecentlyPublished(rnrLastGeneratedDate);
  const {
    data,
    isLoading: isStoriesLoading,
    isSuccess,
  } = useGetSubscriptionStatusQuery();
  const { data: featureKeys } = useGetFeatureKeys();
  const [tags, setTags] = useState<string[]>([]);

  const isStorifyEnabled = featureKeys?.storify_enabled || false;
  const storifyWidgetId = featureKeys?.storify_widget_id || '8612';
  const isPHPRO = kitType === KitType.PHPRO;
  const isGHPRO = kitType === KitType.GHPRO;

  const subscriptionData = useMemo(() => {
    if (!isStorifyEnabled) {
      return;
    }
    // storify tags target the supplement subscription, not the test-kit sub
    const supplementSub = data?.subscriptions?.find(
      (subscription) => (subscription.category ?? '').toUpperCase() !== 'KIT',
    );

    const subscribedProducts = (supplementSub?.products ?? [])
      .filter((item) => item?.is_subscribed)
      .map((element) => mapProductIdToTag(element.id));

    //tag has NOT-SUBSCRIBED, SUBSCRIBED,CANCELED-SUBSCRIBED
    const otherTags = supplementSub?.tag ?? '';

    //CONCIERGE, STANDARD
    const subscriptionType = supplementSub?.category ?? '';

    return { otherTags, subscriptionType, subscribedProducts };
  }, [data, isStorifyEnabled]);

  useEffect(() => {
    if (!isStorifyEnabled) {
      return;
    }
    if (isSingleStory) {
      if (kitType) {
        setTags([`${kitType}-RR`]);
      } else {
        setTags(['RECOMMENDATION-ONBOARDING-FIRST-WEEK']);
      }
    } else {
      setTags((prevTags) => {
        const newTags = [
          ...prevTags,
          isPHPRO ? KitType.FBI : isGHPRO ? KitType.GI : (kitType ?? ''),
        ];
        if (isResultsReleasedRecently) {
          newTags.push('FIRST-WEEK-STORY');
        }
        if (
          isSuccess &&
          subscriptionData?.subscribedProducts &&
          subscriptionData?.subscribedProducts.length > 0
        ) {
          newTags.push(...subscriptionData.subscribedProducts);
        }
        if (subscriptionData?.otherTags) {
          newTags.push(subscriptionData.otherTags);
        }
        if (subscriptionData?.subscriptionType) {
          newTags.push(subscriptionData.subscriptionType);
        }
        if (isInternalTesting) {
          newTags.push('INTERNAL-TESTING');
        }
        newTags.push('ALL-USER');
        return [...new Set(newTags.filter(Boolean))];
      });
    }
  }, [
    isSuccess,
    subscriptionData,
    kitType,
    isStorifyEnabled,
    isPHPRO,
    isGHPRO,
    isResultsReleasedRecently,
    isInternalTesting,
  ]);

  return {
    tags,
    isStoriesLoading,
    isStorifyEnabled,
    storifyWidgetId,
  };
};

export default useStorifyConfig;
